var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const DEFAULT_BATCH_SIZE = 10;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const DEFAULT_MAX_RETRIES = 3;
const MAX_BACKOFF_MS = 30000;
export class HecClient {
    constructor(config) {
        var _a, _b, _c;
        this.buffer = [];
        this.flushTimer = null;
        this.config = {
            endpoint: config.endpoint,
            token: config.token,
            batchSize: (_a = config.batchSize) !== null && _a !== void 0 ? _a : DEFAULT_BATCH_SIZE,
            flushIntervalMs: (_b = config.flushIntervalMs) !== null && _b !== void 0 ? _b : DEFAULT_FLUSH_INTERVAL_MS,
            maxRetries: (_c = config.maxRetries) !== null && _c !== void 0 ? _c : DEFAULT_MAX_RETRIES,
        };
        this.flushTimer = setInterval(() => this.flush(), this.config.flushIntervalMs);
    }
    /** Expose buffer for testing purposes. */
    get _buffer() {
        return this.buffer;
    }
    /** Add an event to the buffer. Triggers flush if batch size reached. */
    enqueue(event) {
        this.buffer.push(event);
        if (this.buffer.length >= this.config.batchSize) {
            this.flush();
        }
    }
    /** Serialize a list of events as newline-delimited JSON. */
    static serialize(events) {
        return events.map((e) => JSON.stringify(e)).join('\n');
    }
    /** Flush all buffered events to Splunk HEC with retry logic. */
    flush() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.buffer.length === 0)
                return;
            // Snapshot and clear — allows new events to accumulate during transmission
            const snapshot = this.buffer.slice();
            this.buffer = [];
            const body = HecClient.serialize(snapshot);
            for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
                try {
                    const response = yield fetch(this.config.endpoint, {
                        method: 'POST',
                        headers: {
                            Authorization: `Splunk ${this.config.token}`,
                            'Content-Type': 'application/json',
                        },
                        body,
                    });
                    if (response.ok)
                        return;
                    // HTTP 4xx/5xx — retryable
                    if (attempt < this.config.maxRetries) {
                        yield this.backoff(attempt);
                    }
                }
                catch (_a) {
                    // Network error — retryable
                    if (attempt < this.config.maxRetries) {
                        yield this.backoff(attempt);
                    }
                }
            }
            // All retries exhausted — discard batch and warn
            console.warn(`[HecClient] Failed to send ${snapshot.length} events after ${this.config.maxRetries} retries. Batch discarded.`);
        });
    }
    /** Flush using navigator.sendBeacon for page unload scenarios. */
    flushSync() {
        if (this.buffer.length === 0)
            return;
        const snapshot = this.buffer.slice();
        this.buffer = [];
        const body = HecClient.serialize(snapshot);
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(this.config.endpoint, blob);
    }
    /** Stop the flush timer and clean up resources. */
    destroy() {
        if (this.flushTimer !== null) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
    }
    backoff(attempt) {
        const delay = Math.min(1000 * Math.pow(2, attempt), MAX_BACKOFF_MS);
        return new Promise((resolve) => setTimeout(resolve, delay));
    }
}
